import React, { useState, useRef, useEffect } from 'react';
import { CaretDown } from '@phosphor-icons/react';

const COMMON_DOMAINS = [
    'gmail.com',
    'hotmail.com',
    'outlook.com',
    'yahoo.com',
    'icloud.com',
    'live.com',
    'me.com',
    'aol.com',
    'protonmail.com',
    'exploremos.mx',
];

/**
 * Email input split into [local-part] @ [domain-picker].
 * Domain field is a free-text input with a dropdown of common domains.
 */
const EmailWithDomainPicker = ({ value = '', onChange, compact = false, ...rest }) => {
    const [local, setLocal] = useState('');
    const [domain, setDomain] = useState('');
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    // Initial sync from external value
    useEffect(() => {
        if (value && value.includes('@')) {
            const [l, d] = value.split('@');
            setLocal(l || '');
            setDomain(d || '');
        } else {
            setLocal(value || '');
            setDomain('');
        }
        // eslint-disable-next-line
    }, [value]);

    // Click outside to close
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const emit = (newLocal, newDomain) => {
        if (!newLocal && !newDomain) {
            onChange('');
        } else {
            onChange(`${newLocal}@${newDomain}`);
        }
    };

    const filtered = COMMON_DOMAINS.filter(
        (d) => !domain || d.toLowerCase().includes(domain.toLowerCase())
    );

    return (
        <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-1" ref={wrapRef}>
            <input
                {...rest}
                type="text"
                value={local}
                onChange={(e) => {
                    const v = e.target.value.replace(/[@\s]/g, '');
                    setLocal(v);
                    emit(v, domain);
                }}
                placeholder="usuario"
                style={{ backgroundColor: '#FFFFFF' }}
                className={`${compact ? 'h-10' : 'h-11'} rounded-md border border-[#ECE3D2] px-2.5 text-sm text-[#1F2D4B] focus:outline-none focus:ring-1 focus:ring-[#1F2D4B] focus:border-[#1F2D4B] placeholder:text-[#9CA3AF]`}
                data-testid="email-local-input"
            />
            <div className="flex items-center justify-center px-1.5 text-[#8B8474] font-semibold text-base">@</div>
            <div className="relative">
                <input
                    type="text"
                    value={domain}
                    onChange={(e) => {
                        const v = e.target.value.replace(/[@\s]/g, '');
                        setDomain(v);
                        emit(local, v);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder="dominio.com"
                    style={{ backgroundColor: '#FFFFFF' }}
                    className={`w-full ${compact ? 'h-10' : 'h-11'} rounded-md border border-[#ECE3D2] pl-2.5 pr-8 text-sm text-[#1F2D4B] focus:outline-none focus:ring-1 focus:ring-[#1F2D4B] focus:border-[#1F2D4B] placeholder:text-[#9CA3AF]`}
                    data-testid="email-domain-input"
                />
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#8B8474] hover:text-[#1F2D4B]"
                    data-testid="email-domain-toggle"
                    tabIndex={-1}
                >
                    <CaretDown size={14} weight="bold" />
                </button>

                {open && filtered.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto bg-white border border-[#ECE3D2] rounded-md shadow-lg">
                        {filtered.map((d) => (
                            <button
                                type="button"
                                key={d}
                                onClick={() => {
                                    setDomain(d);
                                    emit(local, d);
                                    setOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-[#1F2D4B] hover:bg-[#F8F4EC] transition-colors"
                                data-testid={`email-domain-option-${d}`}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailWithDomainPicker;
